import React from "react";
import {
  formatAmount,
  formatCount,
  formatDurationGroup,
  formatDurationShort,
  formatPercent,
} from "../../utils/whatif/whatIfFormat";

// Exported: the Save Scenario tab's own metric row (effort/labour/staffing
// transitions above the to-be RACI matrix) reuses this card styling rather
// than a second copy, so both places read as the same design language.
export const Metric = ({ label, value, note }) => (
  <div className="metric">
    <div className="metric-label">{label}</div>
    <div className="metric-value">{value}</div>
    {note && <div className="metric-note">{note}</div>}
  </div>
);

// As-is and to-be in one tile rather than two: the pair is only meaningful read
// together, and two cards side by side invite reading either as "the" cost.
export const Shift = ({ from, to }) => (
  <span className="wif-metric-shift">
    <span className="wif-metric-from">{from}</span>
    <span className="wif-metric-arrow" aria-hidden="true">
      →
    </span>
    <span className="wif-metric-to">{to}</span>
  </span>
);

// A drop with no stated cause reads as an error. Naming the eliminated tasks is
// the whole explanation for the gap between the two figures above it.
export const eliminationNote = (count) =>
  count > 0
    ? `after removing ${formatCount(count)} NVA task${count === 1 ? "" : "s"}`
    : null;

// Process-level results for the selected process.
const MetricsPanel = ({
  metrics,
  summary,
  warnings,
  eliminatedCount = 0,
  timeUnit,
  currency,
  rates,
}) => {
  const money = (amount) =>
    formatAmount(amount, metrics.currency, currency, rates);

  return (
    <div className="role-card">
      <div className="metrics-grid">
        <Metric
          label="Cycle Time"
          value={formatDurationShort(metrics.cycleTimeMinutes, timeUnit)}
        />
        <Metric
          label="Labor Cost / Case"
          value={
            metrics.laborCostPerCaseToBe != null ? (
              <Shift
                from={money(metrics.laborCostPerCase)}
                to={money(metrics.laborCostPerCaseToBe)}
              />
            ) : (
              money(metrics.laborCostPerCase)
            )
          }
          note={
            metrics.laborCostPerCaseToBe != null
              ? eliminationNote(eliminatedCount)
              : null
          }
        />
        <Metric
          label="Cycle-Time Efficiency"
          value={formatPercent(metrics.cycleTimeEfficiencyPercent)}
        />
        <Metric
          label="Processing / Waiting / Rework"
          value={formatDurationGroup(
            [
              metrics.processingTimeMinutes,
              metrics.waitingTimeMinutes,
              metrics.reworkTimeMinutes,
            ],
            timeUnit,
          )}
        />
        <Metric
          label="Tasks"
          value={
            metrics.subprocessCount > 0
              ? `${formatCount(metrics.taskCount ?? summary.totalTasks)} (${formatCount(metrics.subprocessCount)} subprocesses)`
              : formatCount(summary.totalTasks)
          }
        />
      </div>

      {warnings.length > 0 && (
        <ul className="whatif-warnings">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MetricsPanel;
